import { cn } from '../../lib/utils.js';

// Underlined tab strip for splitting one page's list into views.
//
// Deliberately not Radix: these tabs don't own any panel content, they
// only pick which rows the page renders below them. A roving-tabindex
// widget with aria-controls would be describing a relationship that
// isn't there. This is a row of buttons over a shared hairline, which is
// what it looks like and what it is.
//
// Counts live in the tab rather than in a separate line under the page
// title — the same number in two places drifts, and "where are my
// things" is answered faster by a badge on the tab that holds them.

export interface TabOption<T extends string> {
  value: T;
  label: string;
  /** Rendered as a badge after the label. Omit to show no count. */
  count?: number;
}

export function Tabs<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T;
  options: TabOption<T>[];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex items-center gap-1 border-b border-[hsl(var(--hairline))]',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              // -mb-px pulls each tab down onto the container's border so
              // the active underline replaces the hairline rather than
              // stacking a second line under it.
              '-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 transition-colors',
              'focus:outline-none focus-visible:bg-muted/60',
              active
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="mono-label">{opt.label}</span>
            {opt.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums',
                  active
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
